import React, { ComponentType, useMemo } from 'react';
import {
  usePanorama,
  withPanorama,
  InjectedPanoramaProps,
} from '@wix/yoshi-flow-editor';

export class Interactions {
  private readonly interactions: Record<string, number> = {};

  constructor(
    private readonly logger: InjectedPanoramaProps['panoramaClient'],
  ) {}

  start(interactionName: string) {
    let count = this.interactions[interactionName] || 0;
    count++;
    this.logger.transaction(interactionName).start();
    this.interactions[interactionName] = count;
  }

  end(interactionName: string) {
    let count = this.interactions[interactionName];

    if (count > 0) {
      count--;
      this.logger.transaction(interactionName).finish();
      this.interactions[interactionName] = count;
    }
  }
}

export type WithInteractionProps<P> = InjectedPanoramaProps & {
  interactions: Interactions;
} & P;

export function withPanoramaInteractions<P>(
  Component: ComponentType<WithInteractionProps<P>>,
) {
  return withPanorama<P & InjectedPanoramaProps>((props) => {
    const { panoramaClient } = props;
    const interactions = useMemo(
      () => new Interactions(panoramaClient),
      [panoramaClient],
    );

    return <Component interactions={interactions} {...props} />;
  });
}

export function usePanoramaInteractions() {
  const panorama = usePanorama();

  return useMemo(() => new Interactions(panorama), [panorama]);
}

const playEvents = {
  computer: 'COMPUTER_PLAY',
  vimeo: 'VIMEO_PLAY',
  youtube: 'YOUTUBE_PLAY',
  facebook: 'FACEBOOK_PLAY',
  // By layout
  grid: 'GRID_PLAY',
  classic: 'CLASSIC_PLAY',
  compact: 'COMPACT_PLAY',
  'full-screen-modal': 'FULL_SCREEN_MODAL_PLAY',
  // By trigger
  auto: 'AUTO_PLAY',
  click: 'CLICK_PLAY',
  open: 'OPEN_PLAY',
} as const;

// STRIP layout and SLIDER layout does not support inframe play.
// Such interaction is impossible.
type PlayInteractionOptions = {
  source: 'computer' | 'vimeo' | 'youtube' | 'facebook';
  layout: 'grid' | 'classic' | 'compact' | 'full-screen-modal';
  trigger: 'auto' | 'click' | 'open';
};

export function startPlayInteraction(
  interactions: Interactions,
  options: PlayInteractionOptions,
) {
  interactions.start('PLAY');
  if (options.trigger !== 'auto') {
    interactions.start(playEvents[options.source]);
    interactions.start(playEvents[options.layout]);
  }
  interactions.start(playEvents[options.trigger]);
}

export function endPlayInteraction(
  interactions: Interactions,
  options: Omit<PlayInteractionOptions, 'trigger'>,
) {
  interactions.end('PLAY');
  interactions.end(playEvents[options.source]);
  interactions.end(playEvents[options.layout]);
  // Try to finish all triggers. We don't send not started events. Shouldn't be a problem
  interactions.end(playEvents.click);
  interactions.end(playEvents.auto);
  interactions.end(playEvents.open);
}
